import { useEffect, useState } from "react";
import { waysIn } from "../../../shared/connector-catalog";
import type { ConnectorDTO, ConnectorPolicy } from "../../../shared/types";
import { accessCharacter, connectorRule, DEFAULT_ACCESS_LEVEL } from "../../../shared/types";
import { confirmDialog } from "../../components/Dialog";
import { Switch } from "../../components/Switch";
import { TextLink } from "../../components/TextLink";
import { Badge, Button, ButtonLink, ErrorText, Field, inputCls, Panel } from "../../components/ui";
import { get, patch, post, put } from "../../lib/api";

/** Who reaches a connector and at which level, with each person's own login to it: GET /api/connectors/:id/access. */
export type ConnectorAccess = {
  people: { userId: string; name: string; email: string; kind: string; role: string; level: string; login: "connected" | "lapsed" | null; lastUsedAt: string | null }[];
  agents: { agentId: string; name: string; primary: boolean; level: string }[];
};

/** Loads who has access, again whenever the connector changes. */
export function useConnectorAccess(connector: ConnectorDTO) {
  const [access, setAccess] = useState<ConnectorAccess | null>(null);
  const reload = () => get<ConnectorAccess>(`/api/connectors/${connector.id}/access`).then(setAccess, () => setAccess(null));
  useEffect(() => void reload(), [connector.id, connector.updatedAt]);
  return { access, reload };
}

/** A level's policy: the connector's default, or one of its named levels. */
export const policyOf = (c: ConnectorDTO, level: string): ConnectorPolicy => (level === DEFAULT_ACCESS_LEVEL ? c.policy : (c.levels.find((l) => l.key === level)?.policy ?? c.policy));
/** "Read-only", "Read and write" or "Custom", with the level's own name when it has one. */
export const levelLabel = (c: ConnectorDTO, level: string) => {
  const name = level === DEFAULT_ACCESS_LEVEL ? "default" : (c.levels.find((l) => l.key === level)?.name ?? level);
  const character = accessCharacter(policyOf(c, level));
  return name.toLowerCase() === character.toLowerCase() ? character : `${character} — ${name}`;
};

/** A value to copy: a label, the value, and Copy. */
export function CopyBox({ label, value, mono = true }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-md border border-line bg-fill-subtle p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold tracking-wide text-muted uppercase">{label}</span>
        <TextLink
          className="text-xs"
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "Copied" : "Copy"}
        </TextLink>
      </div>
      <pre className={`text-xs break-all whitespace-pre-wrap ${mono ? "font-mono" : ""}`}>{value}</pre>
    </div>
  );
}

/** The connector's rule in one plain sentence, from how it is set up right now. */
export function ConnectorRule({ connector, access }: { connector: ConnectorDTO; access: ConnectorAccess | null }) {
  const characters = new Set((access?.agents ?? []).map((a) => accessCharacter(policyOf(connector, a.level))));
  const ways = waysIn(connector);
  const rule = connectorRule(connector, {
    agentAccess: characters.size === 1 ? [...characters][0]! : characters.size === 0 ? accessCharacter(connector.policy) : null,
    personalReady: Boolean(ways.token || (ways.signIn && connector.signInReady)),
    personalWay: Boolean(ways.token || ways.signIn),
  });
  return <p className="rounded-xl bg-fill-subtle px-3 py-2 text-sm">{rule}</p>;
}

type TestResult = { ok: boolean; status: number; account: string | null } | { error: string } | null;

/**
 * People's own login, drawn only for a tool that declares one: the switch, the sign-in app or the token's steps, how many
 * have connected, and the admin's own login tried. A tool that lost its ways in still offers the way back to the key.
 */
export function PeoplesLogin({ connector, access, me, onSaved, run, onShowPeople }: {
  connector: ConnectorDTO;
  access: ConnectorAccess | null;
  me: string | null;
  onSaved: () => Promise<void> | void;
  run: (fn: () => Promise<void>) => Promise<void>;
  onShowPeople: () => void;
}) {
  const ways = waysIn(connector);
  const [app, setApp] = useState({ clientId: "", clientSecret: "" });
  const [tested, setTested] = useState<TestResult>(null);
  const own = connector.credentials === "own";
  if (!ways.signIn && !ways.token && !own) return null;
  const usable = Boolean(ways.token || (ways.signIn && connector.signInReady));
  const name = connector.name;
  const toggle = (on: boolean) =>
    run(async () => {
      const sure = await confirmDialog(on
        ? {
            title: `People's runs use their own ${name} login?`,
            body: `From now on each person's runs use only their own ${name} login from My connections, never the workspace key; nobody can use ${name} for themselves until they connect. Agents working on their own keep the workspace key.`,
            confirm: "Turn on",
          }
        : { title: `People's runs back on the workspace key?`, body: `People's runs use the workspace key again, at each person's level. Their own logins stay, unused.`, confirm: "Turn off" });
      if (!sure) return;
      await patch(`/api/connectors/${connector.id}`, { credentials: on ? "own" : "shared" });
      await onSaved();
    });
  const people = access?.people.filter((p) => p.kind === "person") ?? [];
  const connected = people.filter((p) => p.login === "connected").length;
  const mine = access?.people.find((p) => p.userId === me);
  const hint = own
    ? !ways.signIn && !ways.token
      ? `${name} has no way in for people at this address: nobody can connect. ${connector.hasSecrets ? "Turn this off to go back to the workspace key." : "Store a workspace key below, then turn this off."}`
      : connector.workspaceMode === "signin_app"
        ? `On: each person's runs use only their own ${name} login. The workspace key is the sign-in app itself, which serves agents working on their own alone, so this stays on.`
      : !connector.hasSecrets
        ? `On: each person's runs use only their own ${name} login. There is no workspace key, so this stays on until one is stored.`
        : `On: each person's runs use only their own ${name} login, never the workspace key.`
    : !usable
      ? `Off. Set up the sign-in app below first: until then nobody could connect.`
      : connector.hasSecrets
        ? `Off: people's runs use the workspace key at each person's level.`
        : `Off, and there is no workspace key either, so nobody can use ${name} yet: turn this on, or store a key below.`;
  return (
    <Panel title="People's own login" hint={`Each person signs in with their own ${name} account and works with their own permissions there.`}>
      <div className="space-y-4">
        <Switch label={`People's runs use their own ${name} login`} hint={hint} checked={own} disabled={own ? !connector.hasSecrets : !usable} onChange={(on) => void toggle(on)} />
        {ways.signIn && (
          <div className="space-y-3 border-t border-line pt-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">Sign-in app</span>
              {connector.signInReady ? <Badge tone="emerald">set up</Badge> : <Badge tone="amber">not set up</Badge>}
            </div>
            <p className="text-xs text-muted">
              {connector.signInReady ? <>Client ID <code className="break-all">{connector.signInClientId}</code>. People connect with Connect on My connections, which opens {name}'s own sign-in. </> : `Until the app exists nobody can sign in${ways.token ? " and people add a token instead" : ""}. `}
              {ways.signIn.steps} <TextLink href={ways.signIn.consoleUrl}>Open the console</TextLink>
            </p>
            <CopyBox label="Callback URL" value={`${location.origin}/oauth/connect/${connector.slug}`} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Client ID">
                <input autoComplete="off" className={`${inputCls} font-mono`} value={app.clientId} onChange={(e) => setApp({ ...app, clientId: e.target.value })} placeholder={connector.signInClientId ?? ""} />
              </Field>
              <Field label="Client secret">
                <input type="password" autoComplete="off" className={`${inputCls} font-mono`} value={app.clientSecret} onChange={(e) => setApp({ ...app, clientSecret: e.target.value })} />
              </Field>
            </div>
            <Button
              size="sm"
              disabled={!app.clientId.trim() || !app.clientSecret.trim()}
              onClick={() =>
                run(async () => {
                  await put(`/api/connectors/${connector.id}/signin`, app);
                  setApp({ clientId: "", clientSecret: "" });
                  await onSaved();
                })
              }
            >
              {connector.signInReady ? "Replace the sign-in app" : "Save the sign-in app"}
            </Button>
          </div>
        )}
        {ways.token && (
          <div className="space-y-1 border-t border-line pt-4 text-sm">
            <div className="font-medium">{ways.signIn ? "Or a token of their own" : "A token of their own"}</div>
            <p className="text-xs text-muted">{ways.token.label}: {ways.token.steps}</p>
          </div>
        )}
        {own && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4 text-sm">
            <TextLink onClick={onShowPeople}>{`${connected} of ${people.length} ${people.length === 1 ? "person" : "people"} connected`}</TextLink>
            {mine?.login === "connected" ? (
              <div className="flex flex-wrap items-center gap-2">
                {tested && ("error" in tested ? <Badge tone="red">{tested.error}</Badge> : tested.ok ? <Badge tone="emerald">working{tested.account ? ` · ${tested.account}` : ""}</Badge> : <Badge tone="red">HTTP {tested.status}</Badge>)}
                <Button
                  size="sm"
                  onClick={async () => {
                    setTested(null);
                    setTested(await post<{ ok: boolean; status: number; account: string | null }>(`/api/me/connections/${connector.id}/test`, {}).catch((e: Error) => ({ error: e.message })));
                  }}
                >
                  Test my login
                </Button>
              </div>
            ) : mine ? (
              <ButtonLink to="/connections">{mine.login === "lapsed" ? "Reconnect yours" : "Connect yours first"}</ButtonLink>
            ) : (
              <span className="text-xs text-muted">You have no access to {name}: give yourself access in Users to test a login.</span>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
